/* The ⓘ and the one sentence behind it. See `HintPopover.tsx` for why this is a popover rather
 * than a tooltip.
 *
 * Nothing here is invented: the popup wears `shared/ui/MenuSurface`'s surface — `--bg-card` inside a
 * `--border-2` hairline at `--radius-sm` under `--shadow-pop` — because this app has ONE popup, and
 * the trigger takes the exercise card's ⋯ anatomy at the same 18px cell the mock draws its own
 * inline marks at. */

.trigger {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 18px;
  height: 18px;
  padding: var(--space-0);
  font-size: var(--fs-caption);
  line-height: var(--lh-none);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-5px);
}

/* The pointer target the 18px glyph does not carry — WCAG 2.2 SC 2.5.8's floor, bought with a
 * pseudo-element so the row it stands in keeps its height. The same device the phase band's ⋯ and
 * the tape's rest stepper already use. */
.trigger::after {
  position: absolute;
  inset: var(--space-minus-5px);
  content: '';
}

.trigger:hover {
  color: var(--text);
  background: var(--hover-tint);
}

.trigger:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.trigger[data-popup-open] {
  color: var(--text);
  background: var(--bg-inset);
}

/* On the POSITIONER, never the popup: Base UI positions through inline styles here and the popup
 * inside is `position: static`, so a `z-index` there applies to nothing at all. Above the day dock's
 * `z-index: 20`, which is what this most often opens over. */
.positioner {
  z-index: 40;
}

/* ── The surface is `MenuSurface`'s, and only the geometry is this file's ────────────────────
 * `HintPopover.tsx` wears `menuStyles.popup` and this class together. It re-declared nine of that
 * block's ten properties until 7 September 2026 and dropped the tenth —
 * `max-height: var(--available-height, …)`, the one that file exists to argue for — so a ⓘ near the
 * bottom of a 574px panel opened a popup that hung off the viewport with nothing able to scroll to
 * it. A near-duplicate that silently omits the load-bearing declaration is the exact failure the
 * shared module was promoted to prevent.
 *
 * The class is DOUBLED. Two single-class selectors in two CSS Modules are decided by the order the
 * stylesheets land in the document, and that order differs between `vite build` and the dev server
 * (measured 5 August 2026 on `emberButton`), so a tie on `gap` and `padding` would resolve one way
 * in QA and the other in production. `.popup.popup` settles it by specificity in both. Nothing here
 * restates a value the shared block already sets: `width` is not `min-width`/`max-width`, and those
 * two still bound it at 208/320 exactly as they did. */
.popup.popup {
  gap: var(--space-5px);
  width: 264px;
  padding: var(--space-3);
}

.title {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-column);
  text-transform: uppercase;
  color: var(--text-2);
}

.body {
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text);
}

/* The „?" beside a control: the ⓘ's cell with its ring drawn, since the question mark has none. */
.question {
  font-weight: var(--fw-bold);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-mark);
}

.terms {
  display: grid;
  gap: var(--space-5px);
  margin: var(--space-0);
}

.term {
  display: inline;
  font-weight: var(--fw-bold);
  color: var(--text);
}

.term::after {
  content: ': ';
}

.meaning {
  display: inline;
  margin: var(--space-0);
  color: var(--text-2);
}

.termRow {
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
}

.footnote {
  padding-top: var(--space-2);
  color: var(--text-2);
  border-top: var(--border-hairline) solid var(--border);
}
